import React, { useEffect, useState, useRef, useCallback } from "react";
import profile from "../../Assets/Images/undraw_profile.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faTrashAlt, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import axiosHttp from "../../Config";
import { toast } from "react-toastify";
import Loader from "../../Components/Loader";
import ReactPaginate from "react-paginate";
import ReusableModal from "../../Components/Modal";
import SinglePost from "../../Components/SinglePost";
import Pusher from "pusher-js";

const Report = () => {
  const [reportList, setReportList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: 1,
  });
  const [entries, setEntries] = useState(10);
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [postId, setPostId] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [resolvedReports, setResolvedReports] = useState(new Set());
  const [modalLoading, setModalLoading] = useState(false);
  const latestParamsRef = useRef({
    page: 1,
    entries: 10,
    start: "",
    end: ""
  });

  useEffect(() => {
    latestParamsRef.current = {
      page: pageData.currentPage,
      entries,
      start: dateFilter.startDate,
      end: dateFilter.endDate,
    };
  }, [pageData.currentPage, entries, dateFilter.startDate, dateFilter.endDate]);

  const fetchUserReport = useCallback(async (current, count, start, end, suppressLoading = false) => {
    if (!suppressLoading) {
      setLoading(true);
    }

    let url = "";
    if (start && end && start > end) {
      if (!suppressLoading) {
        toast.error("End date cannot be earlier than the start date");
        setLoading(false);
      }
      return;
      // url = `user-reports?page=${current}&per_page=${count}`;
    } else if (start && end && start < end) {
      url = `user-reports?page=${current}&per_page=${count}&startDate=${start}&endDate=${end}`;
    } else {
      url = `user-reports?page=${current}&per_page=${count}`;
    }

    try {
      const getUserReport = await axiosHttp.get(url);
      if (getUserReport.status === 200) {
        let data = getUserReport.data.data;
        let page = getUserReport.data.meta;
        setPageData({
          currentPage: page.currentPage,
          totalPages: page.lastPage,
        });
        setReportList(data);
      } else {
        setReportList([]);
      }
    } catch (error) {
      console.log(error);
      setReportList([]);
    } finally {
      if (!suppressLoading) {
        setLoading(false);
      }
    }
  }, [toast]);
  // Load resolved reports from localStorage on component mount
  useEffect(() => {
    const savedResolvedReports = JSON.parse(localStorage.getItem('resolvedReports') || '[]');
    setResolvedReports(new Set(savedResolvedReports));
  }, []);

  useEffect(() => {
    fetchUserReport(pageData.currentPage, entries);
  }, [fetchUserReport, pageData.currentPage, entries]);

  useEffect(() => {
    if (dateFilter.startDate !== "" && dateFilter.endDate !== "") {
      fetchUserReport(
        pageData.currentPage,
        entries,
        dateFilter.startDate,
        dateFilter.endDate
      );
    }
  }, [fetchUserReport, pageData.currentPage, entries, dateFilter.startDate, dateFilter.endDate]);



  useEffect(() => {
    const pusherKey = process.env.REACT_APP_PUSHER_KEY;
    const pusherCluster = process.env.REACT_APP_PUSHER_CLUSTER;
    const pusherChannel = process.env.REACT_APP_PUSHER_REPORT_CHANNEL;
    const pusherEvent = process.env.REACT_APP_PUSHER_REPORT_EVENT || 'report-updated';
    const fallbackInterval = Number(process.env.REACT_APP_REPORT_FALLBACK_INTERVAL || 5000);

    if (!pusherKey || !pusherChannel) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Pusher configuration missing; falling back to polling for report updates.');
      }
      const interval = setInterval(() => {
        const { page, entries: perPage, start, end } = latestParamsRef.current;
        fetchUserReport(page, perPage, start, end, true);
      }, fallbackInterval);

      return () => clearInterval(interval);
    }

    const options = {
      cluster: pusherCluster || undefined,
      forceTLS: process.env.REACT_APP_PUSHER_FORCE_TLS !== 'false',
    };

    const authEndpoint = process.env.REACT_APP_PUSHER_AUTH_ENDPOINT;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

    if (authEndpoint) {
      options['authEndpoint'] = authEndpoint;
      if (token) {
        options['auth'] = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      }
    }

    const pusher = new Pusher(pusherKey, options);
    const channel = pusher.subscribe(pusherChannel);

    const handleReportEvent = () => {
      const { page, entries: perPage, start, end } = latestParamsRef.current;
      fetchUserReport(page, perPage, start, end, true);
    };

    channel.bind(pusherEvent, handleReportEvent);

    channel.bind('pusher:subscription_error', (status) => {
      console.error('Pusher subscription error', status);
    });

    return () => {
      channel.unbind(pusherEvent, handleReportEvent);
      pusher.unsubscribe(pusherChannel);
      pusher.disconnect();
    };
  }, [fetchUserReport]);

  const handleSubmit = async () => {
    try {
      const blockPost = await axiosHttp.post(`posts/${postId.id}/block`);
      if (blockPost.status === 201) {
        toast.success(blockPost.data.message);
        closeModal();
      }
    } catch (error) {
      toast.error(error.response.data.message);
      console.log(error);
      setLoading(false);
    }
  };

  const handlePageClick = (event) => {
    setPageData({ ...pageData, currentPage: event.selected + 1 });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDateFilter({ ...dateFilter, [name]: value });
  };

  const openModal = async (report) => {
    setShowModal(true);
    setModalLoading(true);
    setPostId(report || {});
    try {
      if (report?.id) {
        const response = await axiosHttp.get(`post-data/${report.id}`);
        if (response.status === 200) {
          let postData = response?.data?.data;
          if (Array.isArray(postData)) {
            postData = postData[0] || {};
          }
          if (postData && typeof postData === "object") {
            setPostId({ ...report, ...postData });
          }
        }
      }
    } catch (error) {
      toast.error("Failed to load post details");
      console.log(error);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setPostId({});
    setModalLoading(false);
    setShowModal(false);
  };

  const deleteHandler = async (id) => {
    setLoading(true);
    try {
      const deletePost = await axiosHttp.post(`posts/${id}/delete`);
      if (deletePost.status === 201) {
        setLoading(true);
        toast.success(deletePost.data.message);
        
        // Remove from resolved reports if it was resolved
        if (resolvedReports.has(id)) {
          const newResolvedReports = new Set([...resolvedReports].filter(reportId => reportId !== id));
          setResolvedReports(newResolvedReports);
          localStorage.setItem('resolvedReports', JSON.stringify([...newResolvedReports]));
        }
        
        // Dispatch custom event to notify sidebar
        window.dispatchEvent(new CustomEvent('reportStatusChanged', { 
          detail: { action: 'deleted', reportId: id } 
        }));
        
        fetchUserReport(pageData.currentPage, entries);
      }
    } catch (error) {
      toast.error(error.response.data.message);
      console.log(error);
      setLoading(false);
    }
  };

  const markAsResolvedHandler = async (id) => {
    try {
      // Add to resolved reports set
      const newResolvedReports = new Set([...resolvedReports, id]);
      setResolvedReports(newResolvedReports);
      
      // Save to localStorage
      localStorage.setItem('resolvedReports', JSON.stringify([...newResolvedReports]));
      
      // Dispatch custom event to notify sidebar
      window.dispatchEvent(new CustomEvent('reportStatusChanged', { 
        detail: { action: 'resolved', reportId: id } 
      }));
      
      toast.success("Report marked as resolved");
      
      // Optionally, you can make an API call here to update the backend
      // const response = await axiosHttp.post(`reports/${id}/resolve`);
      // if (response.status === 200) {
      //   toast.success("Report marked as resolved");
      // }
    } catch (error) {
      toast.error("Failed to mark report as resolved");
      console.log(error);
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <div className="container-fluid top-container">
      <div className="card  card-one-1 mb-5">
        <div className="card-body">
          <h4 className="title mb-5">Report User</h4>
          <div className="row mb-5">
            <div className="col-md-5">
              <h5 className="head">Report List</h5>
            </div>
            <div className="col-md-7 text-right">
              <div className="row align-items-center justify-content-end">
                <div className="col-md-3">
                  <label>Date Filter</label>
                </div>
                <div className="col-md-4">
                  <input
                    className="form-control"
                    type="date"
                    name="startDate"
                    value={dateFilter.startDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-center">
                  <label className="mr-2">-</label>
                  <input
                    className="form-control"
                    type="date"
                    name="endDate"
                    value={dateFilter.endDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table
              className="table table-bordered"
              id="dataTable"
              width="100%"
              cellspacing="0"
            >
              <thead className="bg-col">
                <tr>
                  <th className="border-right-0 ">
                    <input
                      type="checkbox"
                      className="form-control check-box "
                    />
                  </th>
                  <th>User Name</th>
                  <th>Report</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {reportList.length === 0 && (
                  <p className="mt-2">No Data Available</p>
                )}
                {reportList.length > 0 &&
                  reportList.map((report) => {
                    return (
                      <tr key={report.id}>
                        <td className="text-center border-right-0 ">
                          <input
                            type="checkbox"
                            className="form-control check-box "
                          />
                        </td>
                        <td className="border-right-0 ">
                          <img
                            className="img-profile rounded-circle photo mr-2"
                            src={report.pic_url ? report.pic_url : profile}
                            alt="profile"
                          />{" "}
                          {report?.userName}
                        </td>
                        <td className="border-right-0 ">
                          {report?.postReportComment || "-"}
                        </td>

                        <td className="border-right-0 ">
                          {resolvedReports.has(report.id) ? (
                            <span className="badge badge-success">Resolved</span>
                          ) : (
                            <span className="badge badge-warning">Pending</span>
                          )}
                        </td>

                        <td>
                          <span>
                            <FontAwesomeIcon
                              icon={faEye}
                              className="bg-success text-white p-1 rounded mr-3 cursor-pointer"
                              onClick={() => openModal(report)}
                            />
                          </span>
                          {!resolvedReports.has(report.id) && (
                            <span onClick={() => markAsResolvedHandler(report.id)}>
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="bg-info text-white p-1 rounded mr-3 cursor-pointer"
                                title="Mark as Resolved"
                              />
                            </span>
                          )}
                          <span onClick={() => deleteHandler(report.id)}>
                            <FontAwesomeIcon
                              icon={faTrashAlt}
                              className="bg-danger text-white p-1 rounded mr-3 cursor-pointer"
                            />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="d-flex mt-5 justify-content-between ">
            <div className="col-sm-12 col-md-6">
              <label className="d-flex align-items-center">
                Show{" "}
                <select
                  className="form-control entry-select ml-2 mr-2"
                  value={entries}
                  onChange={(e) => setEntries(Number(e.target.value))}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                entries
              </label>
            </div>
            {pageData.totalPages > 1 && (
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={pageData.totalPages}
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                breakClassName="page-item"
                breakLinkClassName="page-link"
                containerClassName="pagination justify-content-end"
                activeClassName="active"
                previousLabel="<"
                forcePage={pageData.currentPage - 1}
              />
            )}
          </div>
        </div>
      </div>
      <ReusableModal
        show={showModal}
        onHide={closeModal}
        title="View Reported Post"
        size="md"
      >
        {modalLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Loader />
          </div>
        ) : (
          <SinglePost post={postId} handleSubmit={handleSubmit} />
        )}
      </ReusableModal>
    </div>
  );
};

export default Report;
